/**
 * permission-guard — Dynamic RBAC permission checks.
 *
 * Architecture:
 *  - PermissionProvider fetches GET /users/me/permissions: the signed-in
 *    user's own resource:action grants, computed by the backend from the same
 *    Casbin policy that enforces every request. (GET /roles and
 *    GET /permissions are admin-only, so a member could never build a map
 *    from them.)
 *  - useHasPermission(resource, action) checks that flat set. superadmin and
 *    admin bypass all checks.
 *  - PermissionGuard conditionally renders children based on useHasPermission.
 *
 * NOTE: This is UI-only. The backend enforces all permissions on every request.
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth, type AuthUser } from '../auth-client';
import { fetchMyPermissions } from '../api-client/permissions-api';

// ─── Context ──────────────────────────────────────────────────────────────────

interface PermissionContextValue {
    hasPermission: (resource: string, action: string) => boolean;
    isLoaded: boolean;
}

const PermissionContext = createContext<PermissionContextValue>({
    hasPermission: () => false,
    isLoaded: false,
});

// ─── Provider ─────────────────────────────────────────────────────────────────

// Tenant admins bypass via this role string. Superadmins bypass via the
// is_superadmin flag on the AuthUser (sourced from users.is_superadmin —
// migration 000040 moved superadmin off the roles table).
const BYPASS_ROLES = ['admin'];

const key = (resource: string, action: string) => `${resource}:${action}`;

// Grants and the exact user object + roles they were fetched for.
interface Fetched {
    user: AuthUser | null;
    rolesKey: string;
    granted: Set<string>; // "resource:action"
}

const NONE: ReadonlySet<string> = new Set();

export function PermissionProvider({ children }: { children: React.ReactNode }) {
    const { user } = useAuth();
    const [fetched, setFetched] = useState<Fetched | null>(null);
    const userRolesKey = (user?.roles ?? []).join(',');

    useEffect(() => {
        if (!user) { setFetched({ user, rolesKey: userRolesKey, granted: new Set() }); return; }

        // A response for a previous user (logout, impersonation switch) must
        // not land after the current one.
        let current = true;
        const done = (granted: Set<string>) => {
            if (current) setFetched({ user, rolesKey: userRolesKey, granted });
        };
        fetchMyPermissions()
            .then((grants) => done(new Set(grants.map((g) => key(g.resource, g.action)))))
            // Fail closed: loaded, with nothing granted.
            .catch(() => done(new Set()));
        return () => { current = false; };
    }, [user, userRolesKey]); // re-fetch when user or their roles change

    // Grants count only for the user they were fetched for. When the user
    // changes (startImpersonation swaps the token and reloads the user in
    // place) nothing is granted and isLoaded is false from the very first
    // render, until that user's own fetch lands — never the previous user's
    // grants for a round-trip.
    const fresh = fetched !== null && fetched.user === user && fetched.rolesKey === userRolesKey;
    const granted = fresh ? fetched.granted : NONE;
    const isLoaded = fresh;

    const hasPermission = (resource: string, action: string): boolean => {
        if (!user) return false;
        if (user.is_superadmin === true) return true;
        if ((user.roles ?? []).some(r => BYPASS_ROLES.includes(r))) return true;
        return granted.has(key(resource, action));
    };

    return (
        <PermissionContext.Provider value={{ hasPermission, isLoaded }}>
            {children}
        </PermissionContext.Provider>
    );
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

/**
 * useHasPermission returns true if the current user has the given
 * resource:action permission via their roles.
 *
 * Superadmin and admin bypass all checks.
 * Results come from the backend's Casbin policy for the caller (GET /users/me/permissions).
 */
export function useHasPermission(resource: string, action: string): boolean {
    const ctx = useContext(PermissionContext);
    return ctx.hasPermission(resource, action);
}

/**
 * usePermissionsLoaded reports whether the caller's permissions have finished
 * loading. Route guards use this to avoid redirecting a user away before their
 * permissions are known (which would otherwise bounce a legitimately-permitted
 * user on a hard refresh).
 */
export function usePermissionsLoaded(): boolean {
    return useContext(PermissionContext).isLoaded;
}

// ─── Component ────────────────────────────────────────────────────────────────

interface PermissionGuardProps {
    resource: string;
    action: string;
    children: React.ReactNode;
    fallback?: React.ReactNode;
}

/**
 * PermissionGuard conditionally renders children based on permission check.
 */
export function PermissionGuard({ resource, action, children, fallback = null }: PermissionGuardProps) {
    const allowed = useHasPermission(resource, action);
    return <>{allowed ? children : fallback}</>;
}
